<template>
  <div class="chatbot-page">
    <div class="header__wrapper">
      <!-- Back button - Title - Last time saved -->
      <header class="header__details">
        <a-button type="link" class="back-button" @click="handleBack">
          <ArrowLeftOutlined />
          Volver
        </a-button>

        <div class="title">
          <h2 class="name">
            <ChatbotChannel :currentChannel="currentChannel" :chatbot="chatbotDetails" />
            {{ chatbotDetails.name || "Cargando..." }}
          </h2>

          <!--Estado de chatbot-->
          <a-tag v-if="chatbotDetails.status" :color="StatusProps[chatbotDetails.status].color">
            {{ StatusProps[chatbotDetails.status].text }}
          </a-tag>
        </div>

        <div v-if="treeNodes.length != 0" class="save-time">
          Guardado por ultima vez:
          {{ dateTimeToSlashFormat(chatbotDetails.updated_at, true) || "..." }}
        </div>
      </header>
    </div>

    <!-- Tabs - Status switch -->
    <a-tabs v-model:activeKey="current" id="chatbot-page-header" centered destroyInactiveTabPane>
      <template #rightExtra>
        <div class="extra-action">
          <a-tag v-if="chatbotDetails?.has_update_api_connector" color="warning">
            <a-tooltip
              title="Se han detectado cambios en los conectores API de este chatbot. Revisa la configuración antes de activarlo."
              placement="topLeft"
              arrowPointAtCenter
            >
              <WarningFilled />
            </a-tooltip>
            Cambios detectados
          </a-tag>
          <a-tag v-else-if="isTransferWithoutQueue" color="orange">
            <a-tooltip
              title="Este chatbot se encontraba utilizando una cola que ha sido eliminada. Podrás volver a activarlo cuando selecciones una nueva cola de transferencia."
              placement="bottomLeft"
              arrowPointAtCenter
            >
              <WarningFilled />
            </a-tooltip>
            Cola Eliminada
          </a-tag>
          <a-tag v-if="chatbotDetails?.deleted_variables" color="warning">
            <a-tooltip
              title="Este chatbot estaba utilizando una variable que ha sido eliminada. Podrás activarlo de nuevo si seleccionas una nueva variable."
              placement="topLeft"
              arrowPointAtCenter
            >
              <WarningFilled />
            </a-tooltip>
            Variable eliminada
          </a-tag>

          <DisableButton
            inTemplate
            :chatbotData="chatbotDetails"
            :disabled="isTransferWithoutQueue"
            @update="fetchChatbot"
          />
        </div>
      </template>

      <a-tab-pane :key="TemplateTabsKey.BuildChatbot" tab="Construir chatbot">
        <tree-section :currentChannel="currentChannel" />
      </a-tab-pane>

      <a-tab-pane :key="TemplateTabsKey.GeneralData" tab="Datos generales">
        <div class="container-tab">
          <a-typography-title :level="5" class="tab__title">Datos generales</a-typography-title>
          <StepInitial
            ref="initialStep"
            from-tree-section
            :key="formKey"
            :disabledInput="isChatbotActive"
            @activate-button="handleActivate"
          />
        </div>
      </a-tab-pane>

      <a-tab-pane :key="TemplateTabsKey.AttentionTime" tab="Horario de atención">
        <div class="container-tab">
          <a-typography-title :level="5" class="tab__title">Horario de atención</a-typography-title>
          <StepConfigTime
            ref="timeStep"
            from-tree-section
            :key="formKey"
            :disabled="isChatbotActive"
            @activate-button="handleActivate"
          />
        </div>
      </a-tab-pane>

      <a-tab-pane :key="TemplateTabsKey.LinkedChannels" tab="Canales vinculados">
        <div class="container-tab">
          <a-typography-title :level="5" class="tab__title">Canales vinculados</a-typography-title>
          <StepChannelSelector
            ref="channelStep"
            from-tree-section
            :key="formKey"
            :selectedChannel="currentChannel?.id"
            :disabled="isChatbotActive"
            :show-warning="showWarning"
            @activate-button="handleActivate"
            validateSchedules
            @handle-validate-schedules="handleValidateSchedules"
          />
        </div>
      </a-tab-pane>

      <a-tab-pane :key="TemplateTabsKey.WaitTimeout" tab="Tiempo de espera">
        <div class="container-tab">
          <a-typography-title :level="5" class="tab__title">Tiempo de espera</a-typography-title>
          <StepTimeout
            ref="timeoutStep"
            from-tree-section
            :key="formKey"
            :disabled="isChatbotActive"
            @activate-button="handleActivate"
          />
        </div>
      </a-tab-pane>
    </a-tabs>

    <footer v-if="current !== TemplateTabsKey.BuildChatbot">
      <a-button @click="handleCancel" :disabled="isChatbotActive"> Cancelar </a-button>
      <a-button
        type="primary"
        @click="handleUpdateChatbot"
        :disabled="isUpdateButtonDisabled || isChatbotActive"
        :loading="loadingOk"
      >
        Actualizar {{ TabSectionName[current] }}
      </a-button>
    </footer>
  </div>
</template>

<script setup>
import { ref, onMounted, provide, computed, watch } from "vue";
import { useStore } from "vuex";
import { useRouter, useRoute } from "vue-router";
import { ArrowLeftOutlined, WarningFilled, WarningOutlined } from "@ant-design/icons-vue";
import { message } from "ant-design-vue";

import { fetchChatbotDetails, updateChatbot, validateChannelCustomerSchedule } from "@/app/chatbots/services";
import { StatusProps, TemplateTabsKey, ChatbotStatus, BotType } from "@/app/chatbots/utils/enums";
import { dateTimeToSlashFormat } from "@/app/shared/utils/dates";
import { formatUpdateFormState } from "@/app/chatbots/utils/helpers";
import { ChannelType } from "@/app/shared/utils/enums";
import { fetchVars } from "@/app/manageVars/services";

import TreeSection from "@/app/chatbots/components/sections/TreeSection.vue";
import StepInitial from "@/app/chatbots/components/steps/StepInitial.vue";
import StepConfigTime from "@/app/chatbots/components/steps/StepConfigTime.vue";
import StepChannelSelector from "@/app/chatbots/components/steps/StepChannelSelector.vue";
import DisableButton from "@/app/chatbots/components/buttons/DisableButton.vue";
import ChatbotChannel from "@/app/chatbots/components/icons/ChatbotChannel.vue";
import StepTimeout from "@/app/chatbots/components/steps/StepTimeout.vue";

const TabSectionName = {
  [TemplateTabsKey.BuildChatbot]: "chatbot",
  [TemplateTabsKey.GeneralData]: "datos generales",
  [TemplateTabsKey.AttentionTime]: "horario",
  [TemplateTabsKey.LinkedChannels]: "canales",
  [TemplateTabsKey.WaitTimeout]: "tiempo de espera",
};

const store = useStore();
const route = useRoute();
const router = useRouter();

const current = ref(TemplateTabsKey.BuildChatbot);

const chatbotDetails = ref({});
const queuesOptions = ref([]);
const varsManage = ref();
const loadingOk = ref(false);
const showWarning = ref(false);

const treeNodes = computed(() => store.getters["getChatbotNodes"]);
const isChatbotActive = computed(() => chatbotDetails.value.status === ChatbotStatus.Active);
const isTransferWithoutQueue = computed(() => chatbotDetails.value.deleted_queues);
const currentChannel = computed(() => {
  const channel =
    chatbotDetails.value?.channel || chatbotDetails.value?.channels_customers?.[0]?.channel;

  return {
    icon: channel?.icon,
    name: channel?.name,
    id:
      chatbotDetails.value?.type_bot === BotType.MultiChannel
        ? ChannelType.Multichannel
        : channel?.id,
  };
});

const fetchChatbot = async () => {
  chatbotDetails.value = await fetchChatbotDetails(route.params.id);
  queuesOptions.value = chatbotDetails.value?.channels_customers?.map(({ queues }) => ({
    value: queues?.[0]?.id,
    label: queues?.[0]?.name,
  }));
  const responseVars = await fetchVars({});
  varsManage.value = responseVars.data.results;
};

const addVarInChatbotDetails = (newVar) => {
  chatbotDetails.value.variables.push(newVar);
};
const deleteVarInChatbotDetails = (id) => {
  chatbotDetails.value.variables = chatbotDetails.value.variables.filter((item) => item.id !== id);
};
const addVarManage = (varManage) => {
  varsManage.value.push(varManage);
};

onMounted(async () => {
  await Promise.all([fetchChatbot(), store.dispatch("fetchNodes", route.params.id)]);
});

provide("fetchChatbot", fetchChatbot);
provide("isChatbotActive", isChatbotActive);
provide("chatbotDetails", chatbotDetails);
provide("varsManage", varsManage);
provide("functionsToChatbot", {
  onAddVar: addVarInChatbotDetails,
  onDeleteVar: deleteVarInChatbotDetails,
  onAddVarManage: addVarManage,
});
store.commit("SET_QUEUES_SELECT_OPTIONS", queuesOptions);

const handleBack = () => {
  router.push("/chatbots");
};

// Update Logic
const formKey = ref(1);
const formState = ref({});
const isUpdateButtonDisabled = ref(true);

const initialStep = ref(null);
const timeStep = ref(null);
const channelStep = ref(null);
const timeoutStep = ref(null);

const RefByCurrentTab = {
  [TemplateTabsKey.GeneralData]: initialStep,
  [TemplateTabsKey.AttentionTime]: timeStep,
  [TemplateTabsKey.LinkedChannels]: channelStep,
  [TemplateTabsKey.WaitTimeout]: timeoutStep,
};

const handleActivate = () => {
  isUpdateButtonDisabled.value = false;
};

const handleDisable = () => {
  isUpdateButtonDisabled.value = true;
};

const handleUpdateChatbot = async () => {
  try {
    loadingOk.value = true;
    formState.value = {
      ...RefByCurrentTab[current.value].value?.formState,
    };

    const body = formatUpdateFormState(formState.value);

    await updateChatbot(route.params.id, body);
    await fetchChatbot();
    loadingOk.value = false;
    message.success(`Se ha actualizado el chatbot correctamente `);
    handleDisable();
  } catch (error) {
    loadingOk.value = false;
    const errorMessage =
      error?.getErrorMessage() || "Algo salió mal, por favor, vuelve a intentarlo";
    message.warning(errorMessage);
    console.log(error);
  }
};

const handleCancel = () => {
  formKey.value += 1;
  handleDisable();
  loadingOk.value = false;
};

const handleValidateSchedules = (channel_customers) => {
  // descomentar para habilitar la validación de horarios cuando se necesite
  // console.log("Validating schedules for channel_customers:", channel_customers);
  // validateChannelCustomerSchedule({ channel_customers, bot_id: currentChannel.value?.id })
  //   .then((response) => {
  //     console.log("Validation response:", response);
  //     if (response.conflict) {
  //       showWarning.value = true;
  //     } else {
  //       showWarning.value = false;
  //     }
  //   })
  //   .catch((error) => {
  //     console.error("Error validating channel customer schedules:", error);
  //   });
};

watch(current, () => {
  handleDisable();
});
</script>

<style lang="sass" scoped>
.chatbot-page
  width: 100%
  height: 100%

.header
  &__wrapper
    width: 100%
    padding: 6px 12px 0
    background-color: $white

  &__details
    display: flex
    justify-content: center
    align-items: center
    position: relative
    width: 100%

    .back-button
      padding: 0
      position: absolute
      left: 0

    .title
      display: flex
      align-items: center
      gap: 8px

      .name
        font-size: 16px
        font-weight: 600
        color: $gray-9
        margin: 0
        display: flex

    .save-time
      font-style: italic
      font-weight: 300
      font-size: 12px
      position: absolute
      right: 0

.extra-action
  display: flex

:deep(.ant-tabs-nav)
  background-color: $white
  margin-bottom: 12px !important

:deep(.ant-tabs)
  height: calc(100% - 30px)

:deep(.ant-tabs-content)
  height: 100%

:deep(.ant-tabs-extra-content)
  position: absolute
  right: 12px

:deep(.ant-tabs-tabpane)
  height: 100%

:deep(.ant-tabs-tabpane:not(:first-child))
  background-color: $white

.row
    display: grid
    grid-template-columns: 0.2fr 1fr
    gap: 0px
.container
  &-tree
    padding: 12px
    height: 100%

  &-config
    height: 100%

  &-tab
    margin: 0 auto
    max-width: 50%
    height: calc(100% - 80px)

    .tab__title
      color: $gray-9
      border-bottom: 1px solid $gray-5
      padding: 20px 0 8px
      margin-bottom: 20px

footer
  padding: 14px
  display: flex
  justify-content: flex-end
  gap: 12px
  max-width: 50%
  border-top: 1px solid $gray-5
  margin: -62px auto 0
  position: relative
</style>
